/**
 * features/documents/DocumentSummaryPanel.tsx — สรุปยอดเงินฝั่งขวาของฟอร์ม (sticky)
 *
 * คำนวณด้วย @ft/core ตัวเดียวกับที่ server ใช้ ผู้ใช้จึงเห็นยอดที่ตรงกับที่จะถูกบันทึกจริง
 * (ยกเว้นกรณี VAT ที่ server อาจ override ตาม company.vatEnabled ซึ่งบอกไว้ในหมายเหตุ)
 */

import { computeDocumentTotals, formatMoney } from '@ft/core';
import { Input } from '~/ui/primitives';
import type { DocumentFormState } from './types';

export function DocumentSummaryPanel({
  form,
  onDiscountChange,
  vatEnabled,
  submitSlot,
}: {
  form: DocumentFormState;
  onDiscountChange: (value: number) => void;
  /** true ถ้าบริษัทจด VAT — ใช้ preview เฉยๆ ยอดจริง server เป็นคนคำนวณสุดท้าย */
  vatEnabled: boolean;
  submitSlot?: React.ReactNode;
}) {
  const totals = computeDocumentTotals({
    items: form.items,
    discountAmount: form.discountAmount,
    vatRate: vatEnabled ? 7 : 0,
  });

  return (
    <div className="sticky top-[calc(var(--header-height)+1.25rem)] space-y-4">
      <div className="card p-4">
        <h3 className="mb-3 text-sm font-semibold text-fg">สรุปยอดเงิน</h3>

        <div className="mb-3">
          <label className="label" htmlFor="discountAmount">
            ส่วนลดท้ายบิล (บาท)
          </label>
          <Input
            id="discountAmount"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={form.discountAmount}
            onChange={(event) => onDiscountChange(Number.parseFloat(event.target.value) || 0)}
            className="text-right"
          />
        </div>

        <div className="space-y-1.5 border-t border-border-base pt-3 text-sm">
          <Row label="รวมเป็นเงิน" value={totals.subtotal} />
          {totals.discountAmount > 0 ? (
            <Row label="ส่วนลด" value={-totals.discountAmount} />
          ) : null}
          {vatEnabled ? (
            <>
              <Row label="มูลค่าก่อนภาษี" value={totals.amountBeforeVat} />
              <Row label={`ภาษีมูลค่าเพิ่ม ${totals.vatRate}%`} value={totals.vatAmount} />
            </>
          ) : null}
        </div>

        <div className="mt-3 flex items-baseline justify-between rounded-md bg-[var(--fg)] px-3 py-2.5 text-[var(--bg)]">
          <span className="text-sm">ยอดรวมทั้งสิ้น</span>
          <span className="tabular text-lg font-semibold">{formatMoney(totals.totalAmount)}</span>
        </div>

        {!vatEnabled ? (
          <p className="mt-2 text-xs text-fg-muted">บริษัทไม่ได้จดทะเบียน VAT เอกสารนี้จะไม่มี VAT</p>
        ) : null}
      </div>

      {submitSlot}
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between">
      <span className="text-fg-muted">{label}</span>
      <span className="tabular text-fg">{formatMoney(value)}</span>
    </div>
  );
}
